// Typed wrappers around lib/api for the routes the web app calls.
import { api, ApiError, type ApiErrorBody } from "@/lib/api";
import type {
  AskResponseEnvelope,
  MatterDetail,
  MatterInput,
  MatterSummary,
  ResponseRecord,
  SessionSummary,
  SpeakerRole,
  SpeakerRoleMap,
  Turn,
} from "@/lib/types";

// --- matters ---------------------------------------------------------------

interface ListMattersOptions {
  includeArchived?: boolean;
}

export async function listMatters(
  options: ListMattersOptions = {},
): Promise<MatterDetail[]> {
  const qs = options.includeArchived ? "?includeArchived=true" : "";
  const result = await api<{ matters: MatterDetail[] }>(`/matters${qs}`);
  return result.matters;
}

export async function getMatter(id: string): Promise<MatterDetail> {
  const result = await api<{ matter: MatterDetail }>(`/matters/${id}`);
  return result.matter;
}

export async function createMatter(input: MatterInput): Promise<MatterDetail> {
  const result = await api<{ matter: MatterDetail }>("/matters", {
    method: "POST",
    body: input,
  });
  return result.matter;
}

export async function updateMatter(
  id: string,
  input: Partial<MatterInput> & { archived?: boolean },
): Promise<MatterDetail> {
  const result = await api<{ matter: MatterDetail }>(`/matters/${id}`, {
    method: "PUT",
    body: input,
  });
  return result.matter;
}

export async function deleteMatter(id: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/matters/${id}`, { method: "DELETE" });
}

// --- transcription ---------------------------------------------------------

export interface TranscribeConfig {
  deepgramAvailable: boolean;
  model: string;
  language: string;
  pricePerMinUsd: number;
}

/** Whether Deepgram high-accuracy transcription is available (key configured). */
export async function getTranscribeConfig(): Promise<TranscribeConfig> {
  return api<TranscribeConfig>("/transcribe/config");
}

// --- sessions --------------------------------------------------------------

export async function createSession(
  matterId: string,
  options: { isRehearsal?: boolean } = {},
): Promise<SessionSummary> {
  const result = await api<{ session: SessionSummary }>("/sessions", {
    method: "POST",
    body: { matterId, isRehearsal: options.isRehearsal ?? false },
  });
  return result.session;
}

export interface SessionDetail extends SessionSummary {
  transcriptRevision?: number;
  userId?: string;
  transcript: string;
  matter: MatterSummary;
  /** Persisted responses for this session, oldest first (Section D hydration). */
  responses: ResponseRecord[];
  /** P6 S1: speaker-aware transcript hydration. */
  turns?: Turn[];
  speakerRoles?: SpeakerRoleMap;
}

export async function getSession(sessionId: string): Promise<SessionDetail> {
  const result = await api<{ session: SessionDetail }>(
    `/sessions/${sessionId}`,
  );
  return result.session;
}

export async function patchSessionTranscript(
  sessionId: string,
  transcript: string,
  extra?: {
    turns?: Turn[];
    speakerRoles?: SpeakerRoleMap;
    expectedRevision?: number;
  },
): Promise<{ ok: true; length: number; revision: number }> {
  return api<{ ok: true; length: number; revision: number }>(
    `/sessions/${sessionId}/transcript`,
    {
      method: "PATCH",
      body: {
        transcript,
        expectedRevision: extra?.expectedRevision,
        ...(extra?.turns !== undefined && { turns: extra.turns }),
        ...(extra?.speakerRoles !== undefined && {
          speakerRoles: extra.speakerRoles,
        }),
      },
    },
  );
}

/** P6 S1: tag one speaker's role; persists immediately (read-modify-write on the server). */
export async function setSessionSpeakerRole(
  sessionId: string,
  speaker: number,
  role: SpeakerRole,
): Promise<{ ok: true; speakerRoles: SpeakerRoleMap }> {
  return api<{ ok: true; speakerRoles: SpeakerRoleMap }>(
    `/sessions/${sessionId}/speaker-role`,
    {
      method: "PATCH",
      body: { speaker, role },
    },
  );
}

export async function endSession(sessionId: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/sessions/${sessionId}/end`, { method: "POST" });
}

export interface SessionListItem {
  sharedWithMatter?: boolean;
  review?: string | null;
  canShare?: boolean;
  id: string;
  matterId: string;
  startedAt: string;
  endedAt: string | null;
  /** Courtroom-ready sprint: practice sessions, labelled in the list. */
  isRehearsal?: boolean;
  matter: {
    id: string;
    name: string;
    reference: string | null;
    court: string | null;
  };
  responseCount: number;
}

export async function listSessions(
  matterId?: string,
): Promise<SessionListItem[]> {
  const qs = matterId ? `?matterId=${encodeURIComponent(matterId)}` : "";
  const result = await api<{ sessions: SessionListItem[] }>(`/sessions${qs}`);
  return result.sessions;
}

/** Soft-delete one response (removes it from the live feed; row retained). */
export async function deleteResponse(
  sessionId: string,
  responseId: string,
): Promise<{ ok: true; id: string }> {
  return api<{ ok: true; id: string }>(
    `/sessions/${encodeURIComponent(sessionId)}/responses/${encodeURIComponent(responseId)}`,
    { method: "DELETE" },
  );
}

/** Soft-delete every response in the session (clears the feed). */
export async function clearResponses(
  sessionId: string,
): Promise<{ ok: true; count: number }> {
  return api<{ ok: true; count: number }>(
    `/sessions/${encodeURIComponent(sessionId)}/responses/clear`,
    { method: "POST" },
  );
}

export function exportSessionUrl(
  sessionId: string,
  format: "pdf" | "docx",
): string {
  // Returns the full URL so window.open / anchor target=_blank can hit it.
  // Cookies are sent automatically because the path is same-origin via the
  // dev proxy / prod Nginx.
  const base = (
    (import.meta.env.VITE_API_BASE_URL ?? "/api/v1") as string
  ).replace(/\/$/, "");
  return `${base}/sessions/${sessionId}/export?format=${format}`;
}

// --- matter knowledge (admin / Section F) ----------------------------------

export interface MatterKnowledgeEntrySummary {
  id: string;
  matterId: string;
  title: string;
  sourceType: string;
  tags: string[];
  createdAt: string;
  charCount: number;
}

export interface RecentImport extends MatterKnowledgeEntrySummary {
  matterName: string;
}

export interface CreateKnowledgeInput {
  matterId: string;
  title: string;
  content: string;
  sourceType: string;
  tags: string[];
}

export async function createMatterKnowledge(
  input: CreateKnowledgeInput,
): Promise<MatterKnowledgeEntrySummary> {
  const result = await api<{ entry: MatterKnowledgeEntrySummary }>(
    "/admin/matter-knowledge",
    {
      method: "POST",
      body: input,
    },
  );
  return result.entry;
}

export async function listMatterKnowledge(
  matterId: string,
): Promise<MatterKnowledgeEntrySummary[]> {
  const result = await api<{ entries: MatterKnowledgeEntrySummary[] }>(
    `/admin/matter-knowledge?matterId=${encodeURIComponent(matterId)}`,
  );
  return result.entries;
}

export async function listRecentImports(): Promise<RecentImport[]> {
  const result = await api<{ entries: RecentImport[] }>(
    "/admin/matter-knowledge/recent",
  );
  return result.entries;
}

export async function deleteMatterKnowledge(id: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/admin/matter-knowledge/${id}`, {
    method: "DELETE",
  });
}

// --- legal skills (admin / P2 Section 2) -----------------------------------

export interface LegalSkillSummary {
  slug: string;
  name: string;
  description: string;
  jurisdictions: string[];
  areasOfLaw: string[];
  version: number;
  active: boolean;
  updatedAt: string;
  charCount: number;
}

export interface LegalSkillDetail {
  id: string;
  slug: string;
  name: string;
  description: string;
  body: string;
  jurisdictions: string[];
  areasOfLaw: string[];
  version: number;
  active: boolean;
}

export interface SaveSkillInput {
  slug: string;
  name: string;
  description: string;
  body: string;
  jurisdictions: string[];
  areasOfLaw: string[];
}

export async function listLegalSkills(): Promise<LegalSkillSummary[]> {
  const result = await api<{ skills: LegalSkillSummary[] }>(
    "/admin/legal-skills",
  );
  return result.skills;
}

export async function getLegalSkill(slug: string): Promise<LegalSkillDetail> {
  const result = await api<{ skill: LegalSkillDetail }>(
    `/admin/legal-skills/${encodeURIComponent(slug)}`,
  );
  return result.skill;
}

export async function saveLegalSkill(
  input: SaveSkillInput,
): Promise<LegalSkillSummary> {
  const result = await api<{ skill: LegalSkillSummary }>(
    "/admin/legal-skills",
    {
      method: "POST",
      body: input,
    },
  );
  return result.skill;
}

export async function deleteLegalSkill(slug: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/admin/legal-skills/${encodeURIComponent(slug)}`, {
    method: "DELETE",
  });
}

export interface MatterSkillAssignment {
  slug: string;
  name: string;
  active: boolean;
  priority: number;
}

export async function getMatterSkills(
  matterId: string,
): Promise<MatterSkillAssignment[]> {
  const result = await api<{ assignments: MatterSkillAssignment[] }>(
    `/admin/matters/${encodeURIComponent(matterId)}/skills`,
  );
  return result.assignments;
}

export async function setMatterSkills(
  matterId: string,
  skills: Array<{ slug: string; priority: number }>,
): Promise<{ ok: true; assigned: number; unknown: string[] }> {
  return api<{ ok: true; assigned: number; unknown: string[] }>(
    `/admin/matters/${encodeURIComponent(matterId)}/skills`,
    { method: "POST", body: { skills } },
  );
}

// --- documents (P3 Section 3) ----------------------------------------------

export type DocumentExtractionStatus =
  | "PENDING"
  | "EXTRACTING"
  | "READY"
  | "FAILED";

export interface DocumentSummary {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  extractionStatus: DocumentExtractionStatus;
  extractionError: string | null;
  tokenEstimate: number | null;
  uploadedAt: string;
}

function apiBase(): string {
  return ((import.meta.env.VITE_API_BASE_URL ?? "/api/v1") as string).replace(
    /\/$/,
    "",
  );
}

export async function listMatterDocuments(
  matterId: string,
): Promise<DocumentSummary[]> {
  const result = await api<{ documents: DocumentSummary[] }>(
    `/matters/${encodeURIComponent(matterId)}/documents`,
  );
  return result.documents;
}

// --- battle map (P5 Section 3) ---------------------------------------------

export type AllegationStatus =
  | "ADMITTED"
  | "DENIED"
  | "NOT_ADMITTED"
  | "UNCLEAR";

export interface BattleAllegation {
  id: string;
  orderIndex: number;
  paragraphRef: string;
  claimText: string;
  status: AllegationStatus;
  anthonyPosition: string;
  supportingEvidence: string[];
  contradictingEvidence: string[];
  gaps: string;
  notes: string;
}

export interface BattleMap {
  id: string;
  matterId: string;
  claimDocumentIds: string[];
  defenceDocumentIds: string[];
  model: string | null;
  generatedAt: string;
  allegations: BattleAllegation[];
}

export async function getMatterBattleMap(
  matterId: string,
): Promise<BattleMap | null> {
  const result = await api<{ map: BattleMap | null }>(
    `/matters/${encodeURIComponent(matterId)}/battle-map`,
  );
  return result.map;
}

export async function buildMatterBattleMap(
  matterId: string,
  claimDocumentIds: string[],
  defenceDocumentIds: string[],
): Promise<{
  map: BattleMap | null;
  meta: { durationMs: number; costUsd: number };
}> {
  return api(`/matters/${encodeURIComponent(matterId)}/battle-map`, {
    method: "POST",
    body: { claimDocumentIds, defenceDocumentIds },
  });
}

export async function updateBattleAllegation(
  matterId: string,
  allegationId: string,
  patch: {
    anthonyPosition?: string;
    notes?: string;
    status?: AllegationStatus;
  },
): Promise<BattleMap | null> {
  const result = await api<{ map: BattleMap | null }>(
    `/matters/${encodeURIComponent(matterId)}/battle-map/allegations/${encodeURIComponent(allegationId)}`,
    { method: "PATCH", body: patch },
  );
  return result.map;
}

// --- deadlines (P5 Section 4) ----------------------------------------------

export type DeadlineStatus = "UPCOMING" | "DONE" | "MISSED";

export interface Deadline {
  id: string;
  matterId: string;
  description: string;
  dueDate: string;
  dueDateText: string | null;
  sourceDocumentId: string | null;
  status: DeadlineStatus;
  completedAt: string | null;
}

export interface UpcomingDeadline {
  id: string;
  description: string;
  dueDate: string;
  dueDateText: string | null;
  status: DeadlineStatus;
  matterId: string;
  matter: { name: string };
}

export async function listMatterDeadlines(
  matterId: string,
): Promise<Deadline[]> {
  const r = await api<{ deadlines: Deadline[] }>(
    `/matters/${encodeURIComponent(matterId)}/deadlines`,
  );
  return r.deadlines;
}

export async function createMatterDeadline(
  matterId: string,
  input: { description: string; dueDate: string; dueDateText?: string },
): Promise<Deadline> {
  const r = await api<{ deadline: Deadline }>(
    `/matters/${encodeURIComponent(matterId)}/deadlines`,
    {
      method: "POST",
      body: input,
    },
  );
  return r.deadline;
}

export async function updateMatterDeadline(
  matterId: string,
  id: string,
  patch: { description?: string; dueDate?: string; status?: DeadlineStatus },
): Promise<Deadline[]> {
  const r = await api<{ deadlines: Deadline[] }>(
    `/matters/${encodeURIComponent(matterId)}/deadlines/${encodeURIComponent(id)}`,
    { method: "PATCH", body: patch },
  );
  return r.deadlines;
}

export async function deleteMatterDeadline(
  matterId: string,
  id: string,
): Promise<void> {
  await api<void>(
    `/matters/${encodeURIComponent(matterId)}/deadlines/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
    },
  );
}

export async function extractMatterDeadlines(
  matterId: string,
): Promise<{ scanned: number; created: number; deadlines: Deadline[] }> {
  return api(`/matters/${encodeURIComponent(matterId)}/deadlines/extract`, {
    method: "POST",
  });
}

export async function listUpcomingDeadlines(): Promise<UpcomingDeadline[]> {
  const r = await api<{ deadlines: UpcomingDeadline[] }>(`/deadlines/upcoming`);
  return r.deadlines;
}

/** Upload one file via multipart (the JSON `api` helper can't carry a File). On
 *  a duplicate the server returns 409 (code DUPLICATE) which surfaces as an
 *  ApiError the caller can show as "already uploaded". */
export async function uploadMatterDocument(
  matterId: string,
  file: File,
): Promise<DocumentSummary> {
  const form = new FormData();
  form.append("file", file);
  let res: Response;
  try {
    res = await fetch(
      `${apiBase()}/matters/${encodeURIComponent(matterId)}/documents`,
      {
        method: "POST",
        credentials: "include",
        body: form,
      },
    );
  } catch (err) {
    throw new ApiError(0, {
      code: "NETWORK",
      message: err instanceof Error ? err.message : "Network error",
    });
  }
  const payload = (await res.json().catch(() => null)) as {
    document?: DocumentSummary;
    error?: ApiErrorBody;
  } | null;
  if (!res.ok || !payload?.document) {
    throw new ApiError(res.status, {
      code: payload?.error?.code ?? "UNKNOWN",
      message: payload?.error?.message ?? `HTTP ${res.status}`,
    });
  }
  return payload.document;
}

export async function deleteDocument(id: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/documents/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function reextractDocument(id: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/documents/${encodeURIComponent(id)}/reextract`, {
    method: "POST",
  });
}

/** Full URL for the signed-redirect download, for an anchor target=_blank. */
export function documentDownloadUrl(id: string): string {
  return `${apiBase()}/documents/${encodeURIComponent(id)}/download`;
}

export type AskMode = "fast" | "deep" | "live";

export interface AskInput {
  sessionId: string;
  prompt: string;
  label?: string;
  /** P2 Section 1: fast (no tools, ~2s) vs deep (MCP + AustLII). Default deep.
   *  'live' (Friday Ready P1-4): fast budgets + the Live Assist grounding
   *  directive, for auto-generated suggestions. */
  mode?: AskMode;
  /** Friday Ready P1-4: the client's live transcript tail — fresher than the
   *  5s-debounced server copy, so a live suggestion answers the utterance that
   *  just finished, not the state of play 5 seconds ago. */
  transcriptTail?: string;
}

function askBody(input: AskInput): Record<string, string> {
  const body: Record<string, string> = { prompt: input.prompt };
  if (input.label !== undefined) {
    body.label = input.label;
  }
  if (input.mode !== undefined) {
    body.mode = input.mode;
  }
  if (input.transcriptTail !== undefined) {
    body.transcriptTail = input.transcriptTail;
  }
  return body;
}

export async function ask(input: AskInput): Promise<AskResponseEnvelope> {
  const body = askBody(input);
  return api<AskResponseEnvelope>(`/sessions/${input.sessionId}/ask`, {
    method: "POST",
    body,
  });
}

// --- streaming ask (SSE) ----------------------------------------------------

export interface AskStreamHandlers {
  /** Each text delta as the model writes. Append to the live card. */
  onDelta: (text: string) => void;
  /** Stream finished; carries the saved Response row (same shape as ask()). */
  onDone: (envelope: AskResponseEnvelope) => void;
  /** Stream failed, or the server returned a JSON error instead of a stream. */
  onError: (err: { code: string; message: string }) => void;
}

/**
 * Stream a prompt response over Server-Sent Events.
 *
 * We use fetch + a ReadableStream reader rather than the EventSource API
 * because EventSource is GET only and cannot POST the prompt body (up to 10k
 * chars) or send our auth the way we need. fetch keeps the httpOnly cookie
 * (credentials: 'include') and lets us POST. The server sends `delta`, `done`,
 * and `error` events; see routes/sessions.ts.
 */
export async function askStream(
  input: AskInput,
  handlers: AskStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const base = (
    (import.meta.env.VITE_API_BASE_URL ?? "/api/v1") as string
  ).replace(/\/$/, "");
  const body = askBody(input);

  let res: Response;
  try {
    res = await fetch(`${base}/sessions/${input.sessionId}/ask`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    if (isAbort(err)) {
      return;
    }
    // fetch threw before any response — genuine connectivity loss.
    handlers.onError({
      code: "NO_CONNECTION",
      message: "No connection to the server. Check your internet and retry.",
    });
    return;
  }

  // Server may answer with a JSON error (401, SESSION_ENDED, rate limit) rather
  // than an event stream. A 502/503/504 here is a gateway timeout (nginx killed a
  // slow upstream) — surface it as a distinct TIMEOUT so the UI can suggest fast
  // mode rather than "check your connection".
  const contentType = res.headers.get("content-type") ?? "";
  if (!res.ok || !res.body || !contentType.includes("text/event-stream")) {
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      handlers.onError({
        code: "TIMEOUT",
        message:
          "The response took longer than 5 minutes. Tap retry, or use fast mode for an instant answer.",
      });
      return;
    }
    handlers.onError(await readJsonError(res));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finished = false;

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      for (;;) {
        const sep = buffer.indexOf("\n\n");
        if (sep === -1) {
          break;
        }
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const handled = dispatchFrame(frame, handlers);
        if (handled === "end") {
          finished = true;
        }
      }
    }
  } catch (err) {
    if (isAbort(err)) {
      return;
    }
    handlers.onError({
      code: "STREAM_INTERRUPTED",
      message: "The response was interrupted before it finished. Tap retry.",
    });
    return;
  }

  if (!finished) {
    // Stream closed without a done/error event (e.g. proxy cut it off).
    handlers.onError({
      code: "STREAM_INTERRUPTED",
      message: "The response ended early. Tap retry.",
    });
  }
}

function dispatchFrame(
  frame: string,
  handlers: AskStreamHandlers,
): "end" | "continue" {
  let event: string | null = null;
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith(":")) {
      continue;
    }
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).replace(/^ /, ""));
    }
  }
  if (!event || dataLines.length === 0) {
    return "continue";
  }
  const data = dataLines.join("\n");
  if (event === "delta") {
    const payload = safeJson<{ text?: string }>(data);
    if (payload && typeof payload.text === "string") {
      handlers.onDelta(payload.text);
    }
    return "continue";
  }
  if (event === "done") {
    const payload = safeJson<AskResponseEnvelope>(data);
    if (payload) {
      handlers.onDone(payload);
    }
    return "end";
  }
  if (event === "error") {
    const payload = safeJson<{ code?: string; message?: string }>(data);
    handlers.onError({
      code: payload?.code ?? "UNKNOWN",
      message: payload?.message ?? "The response failed.",
    });
    return "end";
  }
  return "continue";
}

async function readJsonError(
  res: Response,
): Promise<{ code: string; message: string }> {
  try {
    const payload = (await res.json()) as {
      error?: { code?: string; message?: string };
    };
    if (payload.error) {
      return {
        code: payload.error.code ?? "UNKNOWN",
        message: payload.error.message ?? `HTTP ${res.status}`,
      };
    }
  } catch {
    // not JSON
  }
  return { code: "UNKNOWN", message: `HTTP ${res.status}` };
}

function safeJson<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// --- multi-user: admin + activation + settings (P4 Section 2 / 4.2) --------

export type Role = "ADMIN" | "USER";
export type UserStatus = "INVITED" | "ACTIVE" | "DISABLED";
export type ResponseStyle = "PLAIN_ENGLISH" | "PRO";

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  status: UserStatus;
  /** Computed view over status (true when status === 'DISABLED'). */
  disabled: boolean;
  invitedAt: string | null;
  activatedAt: string | null;
  disabledAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  matterCount: number;
  sessionCount: number;
}

export interface AuditEntry {
  id: string;
  userId?: string | null;
  action: string;
  detail: Record<string, unknown>;
  createdAt: string;
}

export async function listUsers(): Promise<AdminUser[]> {
  const result = await api<{ users: AdminUser[] }>("/admin/users");
  return result.users;
}

export interface AdminUserDetail {
  user: AdminUser & {
    invitedBy: { id: string; email: string; name: string | null } | null;
  };
  audit: AuditEntry[];
}

export async function getAdminUser(id: string): Promise<AdminUserDetail> {
  return api<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`);
}

export interface InviteResult {
  user: AdminUser;
  activationUrl: string;
}

export async function inviteUser(input: {
  email: string;
  name?: string;
  role: Role;
}): Promise<InviteResult> {
  return api<InviteResult>("/admin/users", { method: "POST", body: input });
}

/** Create a user with a password (ACTIVE immediately, no activation link).
 *  The password is sent once and never echoed back. */
export async function createUser(input: {
  email: string;
  name?: string;
  role: Role;
  password: string;
}): Promise<{ user: AdminUser }> {
  return api<{ user: AdminUser }>("/admin/users", {
    method: "POST",
    body: input,
  });
}

/** Admin password reset. The new password is entered once, never displayed. */
export async function resetUserPassword(
  id: string,
  password: string,
): Promise<{ user: AdminUser }> {
  return api<{ user: AdminUser }>(
    `/admin/users/${encodeURIComponent(id)}/password`,
    {
      method: "PATCH",
      body: { password },
    },
  );
}

export async function setUserDisabled(
  id: string,
  disabled: boolean,
): Promise<{ user: AdminUser }> {
  return api<{ user: AdminUser }>(
    `/admin/users/${encodeURIComponent(id)}/disabled`,
    {
      method: "PATCH",
      body: { disabled },
    },
  );
}

export async function listAudit(
  params: {
    userId?: string;
    action?: string;
    limit?: number;
  } = {},
): Promise<AuditEntry[]> {
  const qs = new URLSearchParams();
  if (params.userId) qs.set("userId", params.userId);
  if (params.action) qs.set("action", params.action);
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const result = await api<{ audit: AuditEntry[] }>(`/admin/audit${suffix}`);
  return result.audit;
}

// Activation (public, token-gated).
export interface ActivationInfo {
  email: string;
  name: string | null;
}

export async function validateActivation(
  token: string,
): Promise<ActivationInfo> {
  return api<ActivationInfo>(`/auth/activation/${encodeURIComponent(token)}`);
}

export interface ActivatedUser {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  responseStyle: ResponseStyle;
}

export async function activateAccount(
  token: string,
  password: string,
): Promise<ActivatedUser> {
  const result = await api<{ user: ActivatedUser }>(
    `/auth/activation/${encodeURIComponent(token)}`,
    { method: "POST", body: { password } },
  );
  return result.user;
}

// Self-service settings.
export async function updateProfile(input: {
  name?: string | null;
  responseStyle?: ResponseStyle;
}): Promise<{
  user: {
    id: string;
    email: string;
    name: string | null;
    role: Role;
    responseStyle: ResponseStyle;
  };
}> {
  return api(`/auth/me`, { method: "PATCH", body: input });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: true }> {
  return api<{ ok: true }>("/auth/password", {
    method: "POST",
    body: { currentPassword, newPassword },
  });
}

// --- smash features: pre-hearing prep + post-hearing review (P4 Section 3) --

export interface PrepBriefResult {
  brief: string;
  entryId: string | null;
  durationMs: number;
  costUsd: number;
  toolsUsed: Array<{ server: string; tool: string }>;
}

export async function generatePrepBrief(
  matterId: string,
): Promise<PrepBriefResult> {
  return api<PrepBriefResult>(
    `/matters/${encodeURIComponent(matterId)}/prep-brief`,
    {
      method: "POST",
    },
  );
}

export interface SessionReviewResult {
  review: string;
  entryId: string | null;
  skipped: boolean;
  reason?: string;
  costUsd: number;
}

export async function generateSessionReview(
  sessionId: string,
): Promise<SessionReviewResult> {
  return api<SessionReviewResult>(
    `/sessions/${encodeURIComponent(sessionId)}/review`,
    {
      method: "POST",
    },
  );
}

// --- live classification (P3 Section 4) ------------------------------------

export interface LiveSuggestedAction {
  label: string;
  mode: AskMode;
  prompt: string;
}

export interface HearingMomentResult {
  moment: {
    id: string;
    classification: string;
    confidence: number;
    reasoning: string;
    suggestedActions: LiveSuggestedAction[];
    createdAt: string;
  };
  /** Honest measured latency for this classify call. */
  timing: { ms: number };
  /** Estimated cost from real tokens at the (assumed) Haiku rate. */
  cost: { usd: number; rateNote: string };
  tokenCount: number;
  /** False if the model output could not be parsed (returned UNCLEAR). */
  parseOk: boolean;
}

export async function classifySession(
  sessionId: string,
  transcriptWindow: string,
): Promise<HearingMomentResult> {
  return api<HearingMomentResult>(
    `/sessions/${encodeURIComponent(sessionId)}/classify`,
    {
      method: "POST",
      body: { transcriptWindow },
    },
  );
}

// --- Phase 10.6: witnesses, prior statements, cross-exam plans ---------------

export type WitnessRole =
  | "plaintiff_witness"
  | "defendant_witness"
  | "expert"
  | "character"
  | "own_witness"
  | "hostile";
export type WitnessStatus = "pending" | "scheduled" | "examined" | "not_called";
export type StatementSource =
  | "affidavit"
  | "letter"
  | "email"
  | "transcript"
  | "pleading"
  | "media"
  | "other";
export type Reliability = "high" | "medium" | "low";

export interface Witness {
  id: string;
  matterId: string;
  name: string;
  role: WitnessRole;
  affiliation: string | null;
  importance: number;
  status: WitnessStatus;
  scheduledDate: string | null;
  /** Will-say summary / strategic notes. */
  notes: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { priorStatements: number; crossExamPlans: number };
}

export interface PriorStatement {
  id: string;
  witnessId: string;
  source: StatementSource;
  /** Source document + page/paragraph reference, e.g. "Affidavit of X sworn 4 Mar 2025, para 12". */
  sourceCitation: string;
  sourceUrl: string | null;
  /** The exact quoted passage (verbatim). */
  statementText: string;
  topic: string | null;
  dateMade: string | null;
  reliability: Reliability;
  confirmed: boolean;
  createdAt: string;
}

export interface CrossExamPlanSummary {
  id: string;
  title: string;
  status: "draft" | "rehearsed" | "live" | "complete";
  estimatedDuration: number;
  citationsFlagged: number;
  updatedAt: string;
}

export interface WitnessDetail extends Witness {
  priorStatements: PriorStatement[];
  crossExamPlans: CrossExamPlanSummary[];
}

export async function listWitnesses(matterId: string): Promise<Witness[]> {
  const r = await api<{ witnesses: Witness[] }>(
    `/witnesses?matterId=${encodeURIComponent(matterId)}`,
  );
  return r.witnesses;
}

export async function getWitness(id: string): Promise<WitnessDetail> {
  const r = await api<{ witness: WitnessDetail }>(
    `/witnesses/${encodeURIComponent(id)}`,
  );
  return r.witness;
}

export interface WitnessInput {
  name: string;
  role: WitnessRole;
  affiliation?: string | null;
  importance?: number;
  status?: WitnessStatus;
  scheduledDate?: string | null;
  notes?: string;
}

export async function createWitness(
  matterId: string,
  input: WitnessInput,
): Promise<Witness> {
  const r = await api<{ witness: Witness }>("/witnesses", {
    method: "POST",
    body: { matterId, ...input },
  });
  return r.witness;
}

export async function updateWitness(
  id: string,
  input: Partial<WitnessInput>,
): Promise<Witness> {
  const r = await api<{ witness: Witness }>(
    `/witnesses/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      body: input,
    },
  );
  return r.witness;
}

export async function archiveWitness(id: string): Promise<void> {
  await api<{ ok: boolean }>(`/witnesses/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export interface StatementInput {
  source: StatementSource;
  sourceCitation: string;
  sourceUrl?: string | null;
  statementText: string;
  topic?: string | null;
  dateMade?: string | null;
  reliability?: Reliability;
}

export async function createStatement(
  witnessId: string,
  input: StatementInput,
): Promise<PriorStatement> {
  const r = await api<{ statement: PriorStatement }>(
    `/witnesses/${encodeURIComponent(witnessId)}/statements`,
    { method: "POST", body: input },
  );
  return r.statement;
}

export async function updateStatement(
  id: string,
  input: Partial<
    Pick<
      StatementInput,
      "statementText" | "sourceCitation" | "topic" | "reliability"
    >
  > & {
    confirmed?: boolean;
  },
): Promise<PriorStatement> {
  const r = await api<{ statement: PriorStatement }>(
    `/statements/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: input,
    },
  );
  return r.statement;
}

export async function deleteStatement(id: string): Promise<void> {
  await api<{ ok: boolean }>(`/statements/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export interface Contradiction {
  id: string;
  witnessId: string;
  priorStatementId: string;
  documentId: string | null;
  filename: string;
  /** Exact text recovered from the source chunk when verified; the model's
   *  proposed quote (flagged UNVERIFIED) when it could not be located. */
  quotedText: string;
  reasoning: string;
  similarity: number | null;
  verified: boolean;
  status: "candidate" | "accepted" | "dismissed";
  createdAt: string;
}

export interface MapContradictionsSummary {
  witnessId: string;
  statementsScanned: number;
  chunksConsidered: number;
  created: number;
  verified: number;
  unverified: number;
  noDocuments: boolean;
}

export async function mapWitnessContradictions(
  witnessId: string,
): Promise<MapContradictionsSummary> {
  return api<MapContradictionsSummary>(
    `/witnesses/${encodeURIComponent(witnessId)}/contradictions/map`,
    { method: "POST", body: {} },
  );
}

export async function listContradictions(
  witnessId: string,
): Promise<Contradiction[]> {
  const r = await api<{ contradictions: Contradiction[] }>(
    `/witnesses/${encodeURIComponent(witnessId)}/contradictions`,
  );
  return r.contradictions;
}

export async function updateContradiction(
  id: string,
  status: Contradiction["status"],
): Promise<Contradiction> {
  const r = await api<{ contradiction: Contradiction }>(
    `/contradictions/${encodeURIComponent(id)}`,
    { method: "PATCH", body: { status } },
  );
  return r.contradiction;
}

export interface EscapeRoute {
  dodge: string;
  followUp: string;
}

export interface QuestionStep {
  sequenceNumber: number;
  questionText: string;
  expectedAnswer: string;
  escapeRoutes: EscapeRoute[];
  supportingExhibit?: string | null;
  admissionSought: string;
  riskLevel: "safe" | "medium" | "high";
}

export interface TrapWarning {
  description: string;
  severity: "low" | "medium" | "high";
  alternativeApproach: string;
}

export type PlanStatus = "draft" | "rehearsed" | "live" | "complete";

export interface CrossExamPlan {
  id: string;
  matterId: string;
  witnessId: string;
  title: string;
  /** The single admission sought from the witness. */
  objective: string;
  opening: string;
  questionSequence: QuestionStep[];
  trapsToAvoid: TrapWarning[];
  closingMove: string;
  estimatedDuration: number;
  status: PlanStatus;
  citationsFlagged: number;
  createdAt: string;
  updatedAt: string;
  matter?: { id: string; name: string; reference: string | null };
  witness?: { name: string };
}

export async function getCrossExamPlan(id: string): Promise<CrossExamPlan> {
  const r = await api<{ plan: CrossExamPlan }>(
    `/cross-exam-plans/${encodeURIComponent(id)}`,
  );
  return r.plan;
}

export async function generatePlanForWitness(
  witnessId: string,
  input: {
    objective: string;
    constraints?: string | null;
    maxDuration?: number | null;
  },
): Promise<CrossExamPlan> {
  const r = await api<{ plan: CrossExamPlan }>(
    `/witnesses/${encodeURIComponent(witnessId)}/cross-exam-plans`,
    { method: "POST", body: input },
  );
  return r.plan;
}

export async function updateCrossExamPlan(
  id: string,
  input: Partial<
    Pick<
      CrossExamPlan,
      | "title"
      | "objective"
      | "opening"
      | "closingMove"
      | "estimatedDuration"
      | "status"
      | "questionSequence"
      | "trapsToAvoid"
    >
  >,
): Promise<CrossExamPlan> {
  const r = await api<{ plan: CrossExamPlan }>(
    `/cross-exam-plans/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      body: input,
    },
  );
  return r.plan;
}

export async function deleteCrossExamPlan(id: string): Promise<void> {
  await api<{ ok: boolean }>(`/cross-exam-plans/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function recheckPlanTraps(id: string): Promise<TrapWarning[]> {
  const r = await api<{ traps: TrapWarning[] }>(
    `/cross-exam-plans/${encodeURIComponent(id)}/check-traps`,
    { method: "POST", body: {} },
  );
  return r.traps;
}

/** Browser download URL for the plan export (same-origin; cookie auth). */
export function planExportUrl(id: string, format: "pdf" | "docx"): string {
  return `${apiBase()}/cross-exam-plans/${encodeURIComponent(id)}/export?format=${format}`;
}

export interface WitnessContradictionResult {
  conflict: boolean;
  reason?: string;
  suggestedQuestion?: string;
  todayQuote?: string;
  priorQuote?: string;
  source?: string;
  documentName?: string;
  dateMade?: string | null;
  statementId?: string;
  verified: boolean;
  explanation?: string;
}

export async function runWitnessContradiction(
  sessionId: string,
  witnessId: string,
  transcriptSegment: string,
): Promise<{ result: WitnessContradictionResult; timing: { ms: number } }> {
  return api(
    `/sessions/${encodeURIComponent(sessionId)}/witness-contradiction`,
    {
      method: "POST",
      body: { witnessId, transcriptSegment },
    },
  );
}

// --- Legal Brain Phase A: authorities ----------------------------------------

export type AuthorityType = "LEGISLATION" | "CASE";

export interface AuthoritySummary {
  id: string;
  type: AuthorityType;
  jurisdiction: string;
  title: string;
  citation: string;
  sourceUrl: string | null;
  provisionOrParagraph: string | null;
  createdAt: string;
  updatedAt: string;
  chunkCount: number;
  embeddedCount: number;
  pins: Array<{ matterId: string; matterName: string }>;
}

export interface AuthorityMetadataInput {
  type: AuthorityType;
  jurisdiction: string;
  title: string;
  citation: string;
  provisionOrParagraph?: string;
}

export interface AuthorityIngestResult {
  authority: Pick<
    AuthoritySummary,
    | "id"
    | "type"
    | "jurisdiction"
    | "title"
    | "citation"
    | "sourceUrl"
    | "provisionOrParagraph"
  >;
  action: "created" | "replaced" | "metadata-updated" | "unchanged";
  chunks: number;
  embedded: boolean;
  embedError?: string;
}

export async function listAuthorities(): Promise<AuthoritySummary[]> {
  const r = await api<{ authorities: AuthoritySummary[] }>("/authorities");
  return r.authorities;
}

/** Upload one authority file via multipart (PDF/DOCX/TXT/HTML). */
export async function uploadAuthority(
  file: File,
  meta: AuthorityMetadataInput,
): Promise<AuthorityIngestResult> {
  const form = new FormData();
  form.append("file", file);
  form.append("type", meta.type);
  form.append("jurisdiction", meta.jurisdiction);
  form.append("title", meta.title);
  form.append("citation", meta.citation);
  if (meta.provisionOrParagraph) {
    form.append("provisionOrParagraph", meta.provisionOrParagraph);
  }
  let res: Response;
  try {
    res = await fetch(`${apiBase()}/authorities/upload`, {
      method: "POST",
      credentials: "include",
      body: form,
    });
  } catch (err) {
    throw new ApiError(0, {
      code: "NETWORK",
      message: err instanceof Error ? err.message : "Network error",
    });
  }
  const payload = (await res.json().catch(() => null)) as
    | (AuthorityIngestResult & { error?: ApiErrorBody })
    | null;
  if (!res.ok || !payload?.authority) {
    throw new ApiError(res.status, {
      code: payload?.error?.code ?? "UNKNOWN",
      message: payload?.error?.message ?? `HTTP ${res.status}`,
    });
  }
  return payload;
}

export async function fetchAuthorityFromUrl(
  url: string,
  meta: AuthorityMetadataInput,
): Promise<AuthorityIngestResult> {
  return api<AuthorityIngestResult>("/authorities/fetch", {
    method: "POST",
    body: { url, ...meta },
  });
}

export async function reingestAuthority(
  id: string,
): Promise<AuthorityIngestResult> {
  return api<AuthorityIngestResult>(
    `/authorities/${encodeURIComponent(id)}/reingest`,
    {
      method: "POST",
    },
  );
}

export async function deleteAuthority(id: string): Promise<void> {
  await api<undefined>(`/authorities/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function pinAuthority(
  id: string,
  matterId: string,
): Promise<void> {
  await api(`/authorities/${encodeURIComponent(id)}/pins`, {
    method: "POST",
    body: { matterId },
  });
}

export async function unpinAuthority(
  id: string,
  matterId: string,
): Promise<void> {
  await api<undefined>(
    `/authorities/${encodeURIComponent(id)}/pins/${encodeURIComponent(matterId)}`,
    { method: "DELETE" },
  );
}

// --- Legal Brain Phase B: feedback + learning ---------------------------------

export type FeedbackRating = "UP" | "DOWN";

export async function setResponseFeedback(
  sessionId: string,
  responseId: string,
  rating: FeedbackRating,
  note?: string,
): Promise<{ rating: FeedbackRating; note: string | null }> {
  const r = await api<{
    feedback: { rating: FeedbackRating; note: string | null };
  }>(
    `/sessions/${encodeURIComponent(sessionId)}/responses/${encodeURIComponent(responseId)}/feedback`,
    { method: "PUT", body: { rating, ...(note ? { note } : {}) } },
  );
  return r.feedback;
}

export async function clearResponseFeedback(
  sessionId: string,
  responseId: string,
): Promise<void> {
  await api<undefined>(
    `/sessions/${encodeURIComponent(sessionId)}/responses/${encodeURIComponent(responseId)}/feedback`,
    { method: "DELETE" },
  );
}

export interface LearningRunSummary {
  id: string;
  trigger: "SCHEDULED" | "MANUAL";
  startedAt: string;
  finishedAt: string | null;
  gapsReviewed: number;
  itemsFetched: number;
  itemsIngested: number;
  itemsFailed: number;
  selfTestPassed: number;
  selfTestFailed: number;
  reportText: string;
}

export interface LearningOverview {
  cronEnabled: boolean;
  cronHourMelbourne: number;
  cycleRunning: boolean;
  latestRun: LearningRunSummary | null;
  blocked: Array<{
    id: string;
    citation: string;
    titleGuess: string | null;
    type: "LEGISLATION" | "CASE";
    lastError: string | null;
    updatedAt: string;
  }>;
  failedCount: number;
  gaps: Array<{
    id: string;
    topic: string;
    status: "OPEN" | "QUEUED";
    repeatCount: number;
    updatedAt: string;
  }>;
  queue: Partial<
    Record<"PENDING" | "FETCHING" | "INGESTED" | "FAILED" | "BLOCKED", number>
  >;
  corpus: {
    authorities: number;
    chunks: number;
    growth: Array<{ week: string; authorities: number }>;
  };
}

export async function getLearningOverview(): Promise<LearningOverview> {
  return api<LearningOverview>("/learning/overview");
}

export async function listLearningRuns(): Promise<LearningRunSummary[]> {
  const r = await api<{ runs: LearningRunSummary[] }>("/learning/runs");
  return r.runs;
}

export async function listLearningProposals(): Promise<
  Array<{ name: string; content: string }>
> {
  const r = await api<{ proposals: Array<{ name: string; content: string }> }>(
    "/learning/proposals",
  );
  return r.proposals;
}

export async function runLearningCycleNow(): Promise<void> {
  await api("/learning/run", { method: "POST" });
}

// --- rehearsal pressure test (Operation Friday Ready P1-5) -------------------

export interface PressureTestEnvelope {
  response: ResponseRecord;
  timing: { ms: number };
}

/** Ask the rehearsal judge for the next probing, document-grounded question. */
export async function pressureTestQuestion(
  sessionId: string,
): Promise<PressureTestEnvelope> {
  return api<PressureTestEnvelope>(
    `/sessions/${sessionId}/pressure-test/question`,
    {
      method: "POST",
    },
  );
}

/** Submit the operator's spoken answer (live transcript tail) for assessment. */
export async function pressureTestAssess(
  sessionId: string,
  question: string,
  answerTranscript: string,
): Promise<PressureTestEnvelope> {
  return api<PressureTestEnvelope>(
    `/sessions/${sessionId}/pressure-test/assess`,
    {
      method: "POST",
      body: { question, answerTranscript },
    },
  );
}
